import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from './auth.service'; // Importez votre service d'authentification
import { OnboardingService } from './onboarding.service';

export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService); // Injectez le service d'authentification
  const router = inject(Router); // Injectez le service de routage

  // Vérifiez si l'utilisateur est authentifié
  if (authService.getTokenFromCookies()) {
    return true; // Autoriser l'accès si l'utilisateur est connecté
  }

  // Si l'utilisateur n'est pas connecté, redirigez vers la page de connexion
  router.navigate(['/login']);
  return false; // Bloquez l'accès
};

/**
 * Guard de la racine `/` :
 *  - Si l'utilisateur est connecté → redirige vers /Accueil (dashboard refondu)
 *  - Sinon → laisse passer vers WelcomeComponent (landing publique)
 *
 * Évite la confusion « la page d'accueil » entre la landing publique et le
 * tableau de bord du vendeur connecté.
 */
export const rootRedirectGuard: CanActivateFn = (): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  if (authService.getTokenFromCookies()) {
    return router.createUrlTree(['/Accueil']);
  }
  return true;
};

/**
 * Guard de DÉTECTION AUTOMATIQUE de l'onboarding vendeur.
 *
 * Appliqué à la destination d'atterrissage (`/Accueil`). Quand un VENDEUR se
 * (re)connecte alors que son profil n'est pas encore vérifié/validé, on le
 * renvoie automatiquement sur le wizard `/CompleteSignUp` pour qu'il reprenne
 * là où il s'était arrêté — au lieu de le laisser sur un dashboard sans point
 * d'entrée visible vers la vérification.
 *
 * Règles :
 *  - Pas de token → ``true`` (c'est ``authGuard`` qui redirige vers /login).
 *  - Rôle ≠ vendor → ``true`` (admin/staff/employé non concernés par ce gate).
 *  - Statut ``incomplete`` ou ``rejected`` (action requise du vendeur) →
 *    redirection vers /CompleteSignUp.
 *  - ``pending_review`` / ``suspended`` / approuvé → ``true`` (rien à compléter
 *    ici ; la bannière globale reste affichée comme rappel/suivi).
 *  - Erreur API → ``true`` (fail-open : on ne bloque JAMAIS l'app si l'état
 *    d'onboarding est indisponible).
 */
export const onboardingRedirectGuard: CanActivateFn = ():
  | boolean
  | UrlTree
  | Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const onboarding = inject(OnboardingService);

  if (!authService.getTokenFromCookies()) return true;
  const roles = authService.getRolesFromToken();
  if (!roles.includes('vendor')) return true;

  return onboarding.getState().pipe(
    map((st): boolean | UrlTree => {
      if (
        !st.can_sell &&
        (st.vendor_status === 'incomplete' || st.vendor_status === 'rejected')
      ) {
        return router.createUrlTree(['/CompleteSignUp']);
      }
      return true;
    }),
    catchError(() => of(true as boolean)),
  );
};

/**
 * Guard de l'espace STAFF vendeur.site (`/staff/*`, hors login).
 *
 * Exige un token valide ET un rôle `staff` (ou `admin`, super-utilisateur
 * plateforme). Sinon redirige vers /staff/login. L'accès réseau (VPN) est, lui,
 * imposé au bord par Traefik (vpn-only@file) — ce guard ne gère que le rôle.
 */
export const staffGuard: CanActivateFn = (): boolean | UrlTree => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const token = authService.getTokenFromCookies();
  const roles = authService.getRolesFromToken();
  if (
    token &&
    (roles.includes('staff') || roles.includes('admin') || roles.includes('support'))
  ) {
    return true;
  }
  return router.createUrlTree(['/staff/login']);
};
